import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, MenuItem, FormControlLabel, Switch, Box, Typography
} from "@mui/material";
import { Polyline } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import FileInput from "../forms/FileInput";
import MapContainer from "../MapContainer";

const INPUT_TYPES = ["POLYLINE", "GPX", "SESSION"];

const emptyState = {
    name: "",
    description: "",
    imageUrl: "",
    targetUrl: "",
    show: false,
    inputType: "POLYLINE",
    polyline: "",
    sessionId: "",
};

/**
 * Modale per creare/modificare una community route curata.
 * - create: selettore input (polyline / GPX / session_id) + metadati + anteprima mappa
 * - edit (route valorizzata): solo metadati + visibilità (la geometria non si modifica)
 */
export default function CommunityRouteModal({ open, onClose, onSubmit, route, organizationId }) {

    const { t } = useTranslation();
    const editMode = !!route;
    const [state, setState] = useState(emptyState);
    const [gpxFile, setGpxFile] = useState(null);

    useEffect(() => {
        if (!open) return;
        if (editMode) {
            setState({
                ...emptyState,
                name: route.name || "",
                description: route.description || "",
                imageUrl: route.imageUrl || "",
                targetUrl: route.targetUrl || "",
                show: !!route.show,
            });
        } else {
            setState(emptyState);
        }
        setGpxFile(null);
    }, [open, route, editMode]);

    const set = (field) => (e) => setState(s => ({ ...s, [field]: e.target.value }));

    const previewPath = () => {
        const encoded = editMode ? route?.route : (state.inputType === "POLYLINE" ? state.polyline : null);
        if (!encoded || !window.google?.maps?.geometry?.encoding) return null;
        try {
            // La polyline può essere multi-segmento (un blocco per leg, uniti da "€"):
            // decodifico ogni segmento e concateno.
            const path = encoded.split("€")
                .filter(s => s.length > 0)
                .flatMap(s => window.google.maps.geometry.encoding.decodePath(s));
            return path.length ? path : null;
        } catch (e) {
            return null;
        }
    };

    const path = previewPath();
    const center = path ? { lat: path[0].lat(), lng: path[0].lng() } : undefined;

    const handleSubmit = () => {
        const meta = {
            name: state.name,
            description: state.description,
            imageUrl: state.imageUrl,
            targetUrl: state.targetUrl,
            show: state.show,
        };

        if (editMode) {
            onSubmit({ mode: "update", body: meta });
            return;
        }

        if (state.inputType === "GPX") {
            const fd = new FormData();
            fd.append("file", gpxFile);
            Object.entries(meta).forEach(([k, v]) => { if (v !== "" && v != null) fd.append(k, v); });
            onSubmit({ mode: "gpx", formData: fd });
            return;
        }

        const body = { ...meta, inputType: state.inputType };
        if (state.inputType === "POLYLINE") body.polyline = state.polyline;
        if (state.inputType === "SESSION") body.sessionId = state.sessionId;
        onSubmit({ mode: "json", body });
    };

    const canSubmit = () => {
        if (!state.name) return false;
        if (editMode) return true;
        if (state.inputType === "POLYLINE") return !!state.polyline;
        if (state.inputType === "SESSION") return !!state.sessionId;
        if (state.inputType === "GPX") return !!gpxFile;
        return false;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{editMode ? t("edit") : t("communityRoutes")}</DialogTitle>
            <DialogContent dividers>
                <TextField fullWidth margin="dense" label={t("name")} value={state.name} onChange={set("name")} />
                <TextField fullWidth margin="dense" label={t("description")} value={state.description}
                    onChange={set("description")} multiline minRows={2} />
                <TextField fullWidth margin="dense" label="targetUrl" value={state.targetUrl} onChange={set("targetUrl")} />

                <Box mt={2} mb={1}>
                    <Typography variant="caption">{t("image")}</Typography>
                    {state.imageUrl &&
                        <Box mb={1}><img src={state.imageUrl} alt="" style={{ maxHeight: 80, borderRadius: 4 }} /></Box>}
                    <FileInput
                        folder={"organizations/communityRoutes"}
                        prefix={organizationId}
                        showLoadedImages={false}
                        onRequestSave={(url) => setState(s => ({ ...s, imageUrl: url || "" }))}
                    />
                </Box>

                {!editMode &&
                    <>
                        <TextField select fullWidth margin="dense" label={t("type")} value={state.inputType}
                            onChange={set("inputType")}>
                            {INPUT_TYPES.map(it => <MenuItem key={it} value={it}>{it}</MenuItem>)}
                        </TextField>

                        {state.inputType === "POLYLINE" &&
                            <TextField fullWidth margin="dense" label="polyline" value={state.polyline}
                                onChange={set("polyline")} multiline minRows={2}
                                helperText={t("polylineSeparatorHint")} />}

                        {state.inputType === "SESSION" &&
                            <TextField fullWidth margin="dense" label="session_id" value={state.sessionId}
                                onChange={set("sessionId")} />}

                        {state.inputType === "GPX" &&
                            <Box mt={1}>
                                <Button variant="outlined" component="label">
                                    {gpxFile ? gpxFile.name : "GPX"}
                                    <input hidden type="file" accept=".gpx,application/gpx+xml"
                                        onChange={(e) => setGpxFile(e.target.files?.[0] || null)} />
                                </Button>
                            </Box>}
                    </>}

                <FormControlLabel sx={{ mt: 1 }}
                    control={<Switch checked={state.show}
                        onChange={(e) => setState(s => ({ ...s, show: e.target.checked }))} />}
                    label={t("show")} />

                {path &&
                    <Box mt={2} sx={{ height: 240, width: "100%" }}>
                        <MapContainer zoom={12} center={center}>
                            <Polyline path={path} options={{ strokeColor: "#e8340b", strokeWeight: 4 }} />
                        </MapContainer>
                    </Box>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("cancel")}</Button>
                <Button variant="contained" disabled={!canSubmit()} onClick={handleSubmit}>{t("save")}</Button>
            </DialogActions>
        </Dialog>
    );
}
